/**
 * Polling cadences for every query whose data changes server-side.
 *
 * Realtime is moving to a socket (lib/socket.ts, see
 * creative-os-backend/src/infrastructure/Services/Socket/README.md) one page
 * at a time; Brand Universe, the Workshop, the Content Calendar and Market
 * Signals are on it.
 * Migrated queries poll at POLL_SOCKET_SAFETY_MS while their brand is live and
 * fall back to these tiers when it is not. Everything
 * else still treats polling as the realtime layer, so the rule stands: a
 * surface that can change without a user action in this tab never stops
 * polling entirely while in a non-terminal state — it only slows down.
 * Predicates pick the tier; `false` is reserved for genuinely terminal or
 * static data.
 */

/**
 * Socket-driven queries while their brand is live: a slow net for writes no
 * event announces yet. Events and the resync on reconnect do the real work.
 */
export const POLL_SOCKET_SAFETY_MS = 5 * 60_000;

/** Agents/workers are actively producing something the user is watching for. */
export const POLL_MACHINE_MS = 5_000;

/** Waiting on people outside this tab (client review rounds). */
export const POLL_FEEDBACK_MS = 15_000;

/**
 * Nothing visibly running, but crons/teammates/recovery can start work at any
 * moment — the never-stall floor for non-terminal states.
 */
export const POLL_IDLE_MS = 30_000;

/** The client portal's guest view — the other side of the review fence. */
export const POLL_PORTAL_MS = 45_000;

/** Low-urgency curation surfaces (signal inbox, fact proposals). */
export const POLL_CURATION_MS = 60_000;

/**
 * Slow a fast tier down to the idle floor while the tab is hidden. The
 * predicate re-runs on every fetch settle, so hiding clamps within one tick
 * and the focus refetch un-clamps immediately on return. Progress still lands
 * in hidden tabs — just not at machine cadence.
 */
export function clampWhenHidden(ms: number | false): number | false {
  if (ms === false) return false;
  if (typeof document !== "undefined" && document.visibilityState === "hidden") {
    return Math.max(ms, POLL_IDLE_MS);
  }
  return ms;
}
